/**
 * useGetSubscriptionPlans Hook
 * Custom React Query hook for fetching subscription plans
 * Follows the same pattern as useGetEventById
 */

import {useQuery} from 'react-query';
import {SubscriptionPlanDetails} from '~types/subscription/subscription.types';
import {transformApiPlanToDetails, sortPlansByOrder, ApiSubscriptionPlan} from '~services/subscriptionApi';
import {useUserStore} from '~providers/userStore/zustand';
import {BASE_API_URL} from '@env';

/**
 * Fetch subscription plans from the API
 */
async function fetchSubscriptionPlans(token?: string): Promise<SubscriptionPlanDetails[]> {
  const headers: Record<string, string> = {
    'accept': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `${token}`;
  }

  const response = await fetch(`${BASE_API_URL}stripe/subscription/get-plans`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch subscription plans: ${response.status}`);
  }

  const data: ApiSubscriptionPlan[] = await response.json();

  // Transform API response to match SubscriptionPlanDetails structure
  const plans = data.map((apiPlan) => transformApiPlanToDetails(apiPlan));

  // Sort by the order: Reset Table, Culture Edit, All Access
  return sortPlansByOrder(plans);
}

export const useGetSubscriptionPlans = () => {
  return useQuery<SubscriptionPlanDetails[], Error>(
    ['subscriptionPlans'],
    async () => {
      const token = useUserStore.getState().token;
      return fetchSubscriptionPlans(token);
    },
    {
      // Cache subscription plans for 30 minutes
      cacheTime: 1000 * 60 * 30,
      // Consider data fresh for 5 minutes
      staleTime: 1000 * 60 * 5,
      // Don't refetch when window regains focus
      refetchOnWindowFocus: false,
      // Don't refetch on mount if data is still fresh
      refetchOnMount: false,
      // Refetch when network reconnects
      refetchOnReconnect: true,
      // Retry failed requests twice
      retry: 2,
    },
  );
};
